import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/context.tsx";
import type { Locale } from "../../i18n/types.ts";
import styles from "./Header.module.css";

const localeOptions: { value: Locale; flag: string; label: string }[] = [
  { value: "en", flag: "\u{1F1FA}\u{1F1F8}", label: "English" },
  { value: "ja", flag: "\u{1F1EF}\u{1F1F5}", label: "\u65E5\u672C\u8A9E" },
];

interface SportNav {
  key: string;
  label: string;
  basePath: string;
  items: { label: string; path: string; comingSoon?: boolean }[];
}

export function Header() {
  const { t, locale, localePath, stripLocale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [localeOpen, setLocaleOpen] = useState(false);
  const [openSport, setOpenSport] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const localeSwitcherRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const sports: SportNav[] = [
    {
      key: "basketball",
      label: t.nav.basketball,
      basePath: localePath("/basketball"),
      items: [
        { label: t.nav.classic, path: localePath("/basketball/classic") },
        { label: t.nav.ai, path: localePath("/basketball/ai") },
        { label: t.nav.aiTeams, path: localePath("/basketball/aiteam"), comingSoon: true },
      ],
    },
    {
      key: "tennis",
      label: t.nav.tennis,
      basePath: localePath("/tennis"),
      items: [
        { label: t.nav.classic, path: localePath("/tennis/classic") },
        { label: t.nav.ai, path: localePath("/tennis/ai"), comingSoon: true },
      ],
    },
    {
      key: "soccer",
      label: t.nav.soccer,
      basePath: localePath("/soccer"),
      items: [
        { label: t.nav.classic, path: localePath("/soccer/classic") },
        { label: t.nav.ai, path: localePath("/soccer/ai"), comingSoon: true },
      ],
    },
    {
      key: "golf",
      label: t.nav.golf,
      basePath: localePath("/golf"),
      items: [
        { label: t.nav.classic, path: localePath("/golf/classic") },
        { label: t.nav.ai, path: localePath("/golf/ai"), comingSoon: true },
      ],
    },
  ];

  /* Transparent → glass on scroll */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close dropdowns on outside click */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (localeSwitcherRef.current && !localeSwitcherRef.current.contains(e.target as Node)) {
        setLocaleOpen(false);
      }
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenSport(null);
      }
    }
    if (localeOpen || openSport) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [localeOpen, openSport]);

  /* Close mobile menu on route change */
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional trigger on pathname change
  useEffect(() => {
    setMobileOpen(false);
    setOpenSport(null);
  }, [location.pathname]);

  const currentFlag = localeOptions.find((o) => o.value === locale)?.flag;

  /** Navigate to the same page in a different locale */
  function switchLocale(newLocale: Locale) {
    const barePath = stripLocale(location.pathname);
    const newPath = newLocale === "ja" ? `/ja${barePath}` : barePath;
    navigate(newPath);
    setLocaleOpen(false);
  }

  return (
    <header className={`${styles.header} ${scrolled ? styles.headerScrolled : ""}`}>
      <NavLink to={localePath("/basketball/classic")} className={styles.logo}>
        Shoot<span className={styles.logoAccent}>Creator</span>
      </NavLink>

      {/* Mobile hamburger */}
      <button
        type="button"
        className={styles.hamburger}
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label="Toggle menu"
      >
        <span className={`${styles.hamburgerLine} ${mobileOpen ? styles.hamburgerOpen : ""}`} />
      </button>

      <nav ref={navRef} className={`${styles.nav} ${mobileOpen ? styles.navOpen : ""}`}>
        {/* Sport dropdowns */}
        {sports.map((sport) => {
          const isActive = location.pathname.startsWith(sport.basePath);
          const isDropdownOpen = openSport === sport.key;

          return (
            <div key={sport.key} className={styles.sportDropdown}>
              <button
                type="button"
                className={`${styles.sportButton} ${isActive ? styles.sportButtonActive : ""}`}
                onClick={() => setOpenSport(isDropdownOpen ? null : sport.key)}
              >
                {sport.label}
                {sport.items.some((i) => i.comingSoon) && <span className={styles.comingSoonDot} />}
                <span className={`${styles.chevron} ${isDropdownOpen ? styles.chevronOpen : ""}`}>
                  {"\u25BE"}
                </span>
              </button>

              {/* PC: dropdown on click. SP: always visible via CSS */}
              <div className={`${styles.sportMenu} ${isDropdownOpen ? styles.sportMenuOpen : ""}`}>
                {sport.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive: linkActive }) =>
                      `${styles.sportMenuItem} ${linkActive ? styles.sportMenuItemActive : ""}`
                    }
                    onClick={() => {
                      setOpenSport(null);
                      setMobileOpen(false);
                    }}
                  >
                    {item.label}
                    {item.comingSoon && (
                      <span className={styles.comingSoonTag}>{t.nav.comingSoon}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}

        {/* PC: アクティブスポーツのサブナビを直接表示 */}
        {(() => {
          const activeSport = sports.find((s) => location.pathname.startsWith(s.basePath));
          if (!activeSport) return null;
          return (
            <div className={styles.subNav}>
              <span className={styles.subNavSep} />
              {activeSport.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive: linkActive }) =>
                    `${styles.subNavLink} ${linkActive ? styles.subNavLinkActive : ""}`
                  }
                >
                  {item.label}
                  {item.comingSoon && (
                    <span className={styles.comingSoonTag}>{t.nav.comingSoon}</span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })()}

        {/* Locale switcher */}
        <div ref={localeSwitcherRef} className={styles.localeSwitcher}>
          <button
            type="button"
            className={styles.localeButton}
            onClick={() => setLocaleOpen((prev) => !prev)}
            aria-label="Switch language"
          >
            {currentFlag}
          </button>
          {localeOpen && (
            <div className={styles.localeDropdown}>
              {localeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.localeOption} ${
                    locale === option.value ? styles.localeOptionActive : ""
                  }`}
                  onClick={() => switchLocale(option.value)}
                >
                  <span className={styles.localeFlag}>{option.flag}</span>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
